import { useRef, useState } from 'react';
import { useMutation } from '@apollo/client';
import { Formik } from 'formik';
import Button from '../components/Button';
import { Modal, ModalEditableBody, ModalPart } from '../components/Modal';
import { useToast } from '../components/Toast';
import { UPDATE_COMMENT_BODY } from '../utilities/mutations';

const EditCommentBodyModal = ({ commentId, commentBody, onClose }) => {
  const toast = useToast();
  const [updateCommentBody] = useMutation(UPDATE_COMMENT_BODY, {
    variables: { id: commentId },
  });
  const [isUnsaved, setIsUnsaved] = useState(false);
  const [shake, setShake] = useState(false);

  const modalRef = useRef();
  const [scrollFix, setScrollFix] = useState(false);
  const [scrollTop, setScrollTop] = useState(0);

  const [submitting, setSubmitting] = useState(false);

  const closeModal = () => {
    if (!shake && !submitting) {
      if (isUnsaved) {
        setShake(true);
        toast({ children: `Don't forget to save!` });
        setTimeout(() => setShake(false), 500);
      } else {
        if (onClose) {
          onClose();
        }
      }
    }
  };

  const close = () => {
    if (onClose) {
      onClose();
    }
  };

  return (
    <Modal
      ref={modalRef}
      shake={shake}
      onClose={() => closeModal()}
      onScroll={() => {
        if (scrollFix) {
          setScrollFix(false);
          modalRef.current.scrollTop = scrollTop;
        } else {
          setScrollTop(modalRef.current.scrollTop);
        }
      }}
    >
      <Formik
        initialValues={{ body: commentBody }}
        validate={(touched) => {
          if (touched.body !== commentBody) {
            setIsUnsaved(true);
          } else {
            setIsUnsaved(false);
          }
        }}
        onSubmit={async (values) => {
          try {
            setSubmitting(true);
            await updateCommentBody({
              variables: {
                body: values.body,
                id: commentId,
              },
            });
            close();
            setSubmitting(false);
          } catch (error) {
            toast({ children: error.message });
            setSubmitting(false);

            return;
          }
        }}
      >
        {({ values, handleChange, handleBlur, handleSubmit, isSubmitting }) => (
          <form onSubmit={handleSubmit}>
            <ModalEditableBody
              type="text"
              name="body"
              setScrollFix={setScrollFix}
              scrollTop={scrollTop}
              disabled={isSubmitting}
              onChange={handleChange}
              onBlur={handleBlur}
              value={values.body}
              modalRef={modalRef}
            />
            <ModalPart>
              <Button
                type="button"
                disabled={isSubmitting}
                onClick={() => close()}
                isExtended
              >
                Cancel
              </Button>
              <Button
                type="submit"
                disabled={
                  values.body.trim() === commentBody.trim() || isSubmitting
                }
                isPrimary
                isExtended
              >
                Save
              </Button>
            </ModalPart>
          </form>
        )}
      </Formik>
    </Modal>
  );
};

export default EditCommentBodyModal;
